import { api } from './api';
import { PaginatedList, PaginatedRequest } from '../types';

// The settings row is a backend singleton (pk=1); the viewset's get_object()
// ignores the pk, but DRF still needs one on the detail (update) route.
const SETTINGS_ID = 1;

export interface InspectorMotdRecipient {
  id: number;
  name: string;
}

export interface InspectorMotdSettings {
  id: number;
  sender: number | null;
  senderName: string | null;
  sendToAll: boolean;
  recipients: number[];
  recipientsList: InspectorMotdRecipient[];
  updatedAt: string;
}

export interface InspectorMotdSettingsPayload {
  sender: number | null;
  sendToAll: boolean;
  recipients: number[];
}

/** Super Admin: read Settings > Message Setup. */
export const getInspectorMotdSettings = () =>
  api.request<InspectorMotdSettings>({
    method: 'GET',
    url: '/messages/inspector-motd-settings/',
  });

/** Super Admin: designate the sender + audience. */
export const updateInspectorMotdSettings = (
  data: InspectorMotdSettingsPayload,
) =>
  api.request<InspectorMotdSettings>({
    method: 'PUT',
    url: `/messages/inspector-motd-settings/${SETTINGS_ID}/`,
    data,
  });

export interface InspectorChoice {
  id: number;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Super Admin: every active inspector, system-wide.
 *
 * The shared `/account/users/` list is scoped to the caller's franchises and
 * returns nothing for a Super Admin, so Message Setup reads this instead.
 */
export const getInspectorChoices = () =>
  api.request<InspectorChoice[]>({
    method: 'GET',
    url: '/messages/inspector-motd-settings/inspectors/',
  });

export interface InspectorMotdMessage {
  id: number;
  text: string;
  publicationDate: string;
}

/**
 * Designated inspector: send the Message of the Day.
 *
 * No audience argument — it always goes to the whole audience a Super Admin
 * configured. The backend still accepts an explicit `addresses` subset, but
 * nothing in the UI sends one.
 */
export const sendInspectorMotd = (data: { text: string }) =>
  api.request<InspectorMotdMessage>({
    method: 'POST',
    url: '/messages/inspector-motd/',
    data,
  });

/** Designated inspector: today's messages they sent, newest first. */
export const getMyInspectorMotd = () =>
  api.request<InspectorMotdMessage[]>({
    method: 'GET',
    url: '/messages/inspector-motd/',
  });

/** Designated inspector: rewrite the text of a message they sent. */
export const updateInspectorMotd = (
  id: number,
  data: { text: string },
) =>
  api.request<InspectorMotdMessage>({
    method: 'PATCH',
    url: `/messages/${id}/inspector-motd/`,
    data,
  });

export interface InspectorMotdSentRow {
  id: number;
  text: string;
  publicationDate: string;
  addressesList: string;
}

/**
 * Designated inspector: paginated history of every Message of the Day they
 * sent. Shaped like `getMessagesList` so the inspector's Messages page can
 * reuse the Branch Admin's Settings > Messages table as-is.
 */
export const getInspectorMotdSent = (
  params: PaginatedRequest,
) =>
  api.request<PaginatedList<InspectorMotdSentRow>>({
    method: 'GET',
    url: '/messages/inspector-motd/sent/',
    params,
  });

export interface InspectorMotdAudience {
  /** True when a Super Admin set "send to all inspectors". */
  sendToAll: boolean;
  recipients: InspectorChoice[];
}

/**
 * Designated inspector: who the Message of the Day reaches.
 *
 * Read-only. The audience is the Super Admin's call in Settings > Message Setup,
 * so the compose form shows this rather than offering a picker.
 */
export const getInspectorMotdAudience = () =>
  api.request<InspectorMotdAudience>({
    method: 'GET',
    url: '/messages/inspector-motd/audience/',
  });

/** Designated inspector: send a copy of a message they already sent. */
export const reuseInspectorMotd = ({
  id,
}: {
  id: number;
}) =>
  api.request<InspectorMotdMessage>({
    method: 'POST',
    url: `/messages/${id}/resend/`,
  });
